import { io, Socket } from 'socket.io-client';
import { getAuthHeader } from '@/utils/auth';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3001';

export interface PositionUpdateEvent {
  userId: string;
  position: number;
  peopleAhead: number;
  estimatedTime: number;
}

export interface YourTurnSoonEvent {
  userId: string;
  message: string;
  positionsAway: number;
}

export interface YourTurnNowEvent {
  userId: string;
  message: string;
}

export interface QueueStatusChangedEvent {
  queueId: string;
  status: 'active' | 'paused' | 'completed';
}

export interface ErrorEvent {
  code: string;
  message: string;
}

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    const authHeader = getAuthHeader();
    const token = authHeader?.replace('Bearer ', '');

    // For public users, connect without authentication
    const connectionOptions: any = {
      transports: ['websocket', 'polling'], // Add polling as fallback
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    };

    // Only add auth if token exists
    if (token) {
      connectionOptions.auth = { token };
    }

    try {
      this.socket = io(`${WS_URL}/queue`, connectionOptions);
      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.emit('connection:status', { connected: true });
    });

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      this.emit('connection:status', { connected: false });
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.emit('connection:failed', { 
          message: 'Failed to connect to server',
          attempts: this.reconnectAttempts 
        });
      }
    });

    // Queue events
    this.socket.on('position:update', (data: PositionUpdateEvent) => {
      this.emit('position:update', data);
    });

    this.socket.on('your:turn:soon', (data: YourTurnSoonEvent) => {
      this.emit('your:turn:soon', data);
    });

    this.socket.on('your:turn:now', (data: YourTurnNowEvent) => {
      this.emit('your:turn:now', data);
    });

    this.socket.on('queue:status:changed', (data: QueueStatusChangedEvent) => {
      this.emit('queue:status:changed', data);
    });

    // Listen for individual entry status changes
    this.socket.on('status:changed', (data: any) => {
      console.log('Received status:changed event:', data);
      this.emit('status:changed', data);
    });

    // Listen for personal status changes
    this.socket.on('your:status:changed', (data: any) => {
      console.log('Received your:status:changed event:', data);
      this.emit('your:status:changed', data);
    });

    this.socket.on('error', (data: ErrorEvent) => {
      this.emit('error', data);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  subscribeToQueue(queueId: string): void {
    if (!this.socket) {
      console.warn('WebSocket not initialized, attempting to connect...');
      this.connect();
      // Wait a bit for connection to establish
      setTimeout(() => {
        if (this.socket?.connected) {
          this.subscribeToQueue(queueId);
        }
      }, 1000);
      return;
    }

    if (!this.socket.connected) {
      console.warn('WebSocket not connected yet, subscription will be attempted when connected');
      // Store subscription request for when connection is established
      this.socket.once('connect', () => {
        this.subscribeToQueue(queueId);
      });
      return;
    }

    const authHeader = getAuthHeader();
    const token = authHeader?.replace('Bearer ', '');

    this.socket.emit('subscribe:queue', { queueId, token });
  }

  unsubscribeFromQueue(queueId: string): void {
    if (!this.socket) {
      return;
    }

    if (this.socket.connected) {
      this.socket.emit('unsubscribe:queue', { queueId });
    }
  }

  advanceQueue(queueId: string, count: number = 1): void {
    if (!this.socket?.connected) {
      console.error('WebSocket not connected');
      return;
    }

    this.socket.emit('queue:advance', { queueId, count });
  }

  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: Function): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  private emit(event: string, data: any): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getConnectionStatus(): 'connected' | 'disconnected' | 'connecting' {
    if (!this.socket) return 'disconnected';
    if (this.socket.connected) return 'connected';
    return 'connecting';
  }
}

export const webSocketService = new WebSocketService();